import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { REST_API_URL } from "../api/config";
import { buildHeaders } from "../api/buildHeaders";
import { ModalPortal } from "./ModalPortal";

const BASE_URL = REST_API_URL;

const LANGUAGES = [
  { code: "en", label: "English - Inglés", flag: "🇺🇸" },
  { code: "es", label: "Spanish - Español", flag: "🇪🇸" },
];

export function LanguageModal({ isOpen, onClose }) {
  const { i18n, t } = useTranslation();
  const [selected, setSelected] = useState(i18next.language || "en");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelected(i18next.language || "en");
    }
  }, [isOpen]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/auth/change-main-language/`, {
        method: "POST",
        headers: buildHeaders(),
        body: JSON.stringify({ main_language: selected }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data?.detail || data?.message || "Error");
        return;
      }
      i18next.changeLanguage(selected);

      toast.success(t("settings.languageSaved"));
      onClose();
      setTimeout(() => {
        window.location.reload();
      }, 150);
    } catch (err) {
      console.error("Failed to update language:", err);
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalPortal>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="language-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-end sm:items-center z-50 p-4"
          onClick={onClose}
        >
          <motion.div
            key="language-modal"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ type: "spring", damping: 28, stiffness: 320 }}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 text-center mb-5">
                {t("settings.selectLanguage")}
              </h3>

              <div className="flex flex-col gap-2 mb-5">
                {LANGUAGES.map((lang) => {
                  const isActive = selected === lang.code;
                  return (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => setSelected(lang.code)}
                      className={`flex items-center justify-between w-full px-4 py-3.5 rounded-2xl border-2 transition-all ${
                        isActive
                          ? "border-[#020637] bg-[#020637]/5"
                          : "border-gray-200 hover:border-[#020637]/40 bg-white"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl leading-none">
                          {lang.flag}
                        </span>
                        <span
                          className={`text-sm font-medium ${
                            isActive ? "text-[#020637]" : "text-gray-700"
                          }`}
                        >
                          {lang.label}
                        </span>
                      </div>
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                          isActive
                            ? "border-[#020637] bg-[#020637]"
                            : "border-gray-300 bg-white"
                        }`}
                      >
                        {isActive && (
                          <div className="w-2 h-2 rounded-full bg-white" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={handleSave}
                disabled={loading}
                className="w-full py-3.5 rounded-2xl bg-[#020637] hover:bg-[#0e1c7a] active:bg-[#060e4a] text-white font-semibold text-sm transition-colors disabled:opacity-60"
              >
                {loading ? t("settings.saving") : t("settings.save")}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    </ModalPortal>
  );
}
